import { usePreferences } from '@context/preferences.context';
import { useQuery } from '@tanstack/react-query';
import ProductFeedService from '@services/product-feed-service';
import logger from '@helpers/utils/logger/client';
import { useMemo } from 'react';
import mapCatalogHitToProduct from '@helpers/utils/catalog/map-catalog-hit-to-product';
import { CatalogProduct } from '@interfaces/models/catalogProduct';
import useUser from '@hooks/user/use-user';
import { ProductFeedSource } from '@interfaces/api/payloads/product-feed-payload';
import { ProductFeedQueryKeys } from '@enums/react-query-keys';

interface RecommendProductFeeds {
  productID: number;
  source: ProductFeedSource;
}

export interface ParsedProductFeed {
  feedType: string;
  items: CatalogProduct[];
  title: string;
}

export const useRecommendProductFeeds = ({ productID, source }: RecommendProductFeeds) => {
  const { language, currency, country } = usePreferences();
  const { user } = useUser();

  const { data, isLoading } = useQuery({
    queryFn: () => {
      return ProductFeedService.getProductFeeds(
        { productID, locale: { language, currency, country }, source },
        { 'x-userid': user?.id ?? '' },
      );
    },
    queryKey: [ProductFeedQueryKeys.PDP_PRODUCT_FEEDS, productID, language, currency, country, source],
    onError: (error) => {
      logger.error(`Error fetching multiple product feeds on ${source}: ${error}`);
    },
  });

  const parsedProductFeeds = useMemo(() => {
    if (!data) {
      return null;
    }
    return data.feeds.map((feed) => ({
      ...feed,
      items: feed.items.map((item) => mapCatalogHitToProduct(item, country, currency, language)),
    }));
  }, [country, currency, data, language]);

  return { feeds: parsedProductFeeds, isLoading };
};
